<template>
  <div class="sp-finder-result-scheduling" :class="classModifiers" part="base">
    <sp-slot-grid
      v-if="branch === 'grid'"
      v-preserve-properties:entries="entries"
      class="sp-finder-result-scheduling__grid"
      :show-year="showYear"
      @show-more-slots="handleCardsMore"
      @show-full-schedule="requestFullSchedule"
    ></sp-slot-grid>

    <sp-slot-cards
      v-else-if="branch === 'cards'"
      v-preserve-properties:entries="entries"
      v-preserve-properties:max-entries="maxSlots"
      v-preserve-properties:card-elevation="cardElevation"
      :slot-variant="scheduling.slotButtonVariant"
      :more-button-variant="scheduling.moreButtonVariant"
      :full-schedule-placement="scheduling.fullSchedulePlacement"
      :full-schedule-direction="scheduling.fullScheduleDirection"
      :show-year="showYear"
      @show-more-slots="handleCardsMore"
    ></sp-slot-cards>

    <p v-else-if="branch === 'skip'" class="sp-finder-result-scheduling__skip">
      <sp-icon
        v-if="skipDirection"
        class="sp-finder-result-scheduling__skip-icon"
        :name="`gotodate-${skipDirection}`"
      ></sp-icon>
      <span class="sp-finder-result-scheduling__skip-text">
        {{ resultText("closest_appointment_is") }}
        <span class="sp-finder-result-scheduling__date">{{ closestDateLabel }}</span>
        <sp-button variant="appearance-link" class="sp-finder-result-scheduling__skip-link" @click="handleShowDate">{{
          resultText("jump_to_date")
        }}</sp-button>
      </span>
    </p>

    <sp-card
      v-else-if="branch === 'message'"
      class="sp-finder-result-scheduling__card"
      :variant="cardVariant"
      elevation="1"
      sm-variant="plain"
      elevation-sm="0"
    >
      <sp-card-content flex align="center" justify="center">
        <sp-walk-in-message
          v-if="result.walk_in_message"
          class="sp-finder-result-scheduling__walk-in"
          align="center"
          :message="result.walk_in_message"
          :tooltip="resultText('wait_time_tooltip')"
        ></sp-walk-in-message>

        <div v-if="message.multiple" class="sp-finder-result-scheduling__columns">
          <div class="sp-finder-result-scheduling__message --column">
            <h3 class="sp-finder-result-scheduling__patient-type">{{ resultText("new_patients") }}</h3>
            <FinderResultSchedulingMessage :options="message.new_patient" />
          </div>
          <div class="sp-finder-result-scheduling__message --column">
            <h3 class="sp-finder-result-scheduling__patient-type">{{ resultText("returning_patients") }}</h3>
            <FinderResultSchedulingMessage :options="message.returning_patient" />
          </div>
        </div>
        <div v-else class="sp-finder-result-scheduling__message" :class="messageModifiers">
          <FinderResultSchedulingMessage :options="message" />
        </div>
      </sp-card-content>
    </sp-card>

    <sp-button
      v-if="showFullSchedule"
      variant="text"
      densitysmcompact="false"
      class="sp-finder-result-scheduling__full-schedule"
      data-analytics-element="finder-full-schedule"
      data-analytics-interaction="click"
      @click="handleFullSchedule"
    >
      <sp-icon slot="prepend" class="sp-finder-result-scheduling__full-schedule-icon" name="event-filled"></sp-icon>
      {{ resultText("view_full_schedule") }}
    </sp-button>
  </div>
</template>

<script setup>
import { computed } from "vue";
import { vPreserveProperties } from "#ce/utils/property-bindings";
import { formatDatePattern } from "#/shared/lib/helpers/localized-date";
import { resultText } from "#ce/components/SpFinder/lib/result-text";
import { trackControlClick } from "#ce/components/SpFinder/lib/click-analytics";
import { schedulingOptions } from "#ce/components/SpFinder/lib/scheduling-options";
import FinderResultSchedulingMessage from "./FinderResultSchedulingMessage.ce.vue";

const props = defineProps({
  /**
   * The search result: its `appointment_slots`, `closest_available_appointment_date`, `scheduling_type`
   * and `walk_in_message`.
   *
   * @type {Object}
   */
  result: {
    type: Object,
    required: true,
  },
  /**
   * The finder's slot settings:
   *   - slotsUi:                       "grid" renders `sp-slot-grid`, "cards" renders `sp-slot-cards`
   *   - startDate:                     the first date the results show, for the jump direction
   *   - showYear:                      "never", "next_year" or "always"
   *   - maxSlots:                      slots shown per card
   *   - slotButtonVariant, moreButtonVariant, cardElevation,
   *     fullSchedulePlacement, fullScheduleDirection: passed to `sp-slot-cards`
   *   - showMultipleSchedulingTypes:   new and returning patient messages side by side
   *   - doctorMessages:                renders the scheduling message when there are no slots
   *
   * @type {Object}
   */
  scheduling: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(["show-more-slots", "show-date"]);

const showYear = computed(() => props.scheduling.showYear || "never");
const closestDate = computed(() => props.result.closest_available_appointment_date);
const hasSlots = computed(() => Boolean(props.result.appointment_slots));

const branch = computed(() => {
  if (props.scheduling.slotsUi === "grid" && hasSlots.value) {
    return "grid";
  }
  if (props.scheduling.slotsUi === "cards" && hasSlots.value) {
    return "cards";
  }
  if (!hasSlots.value && closestDate.value) {
    return "skip";
  }
  return props.scheduling.doctorMessages === false ? undefined : "message";
});

function optionalNumber(value) {
  return value == null || value === "" ? undefined : Number(value);
}

const maxSlots = computed(() => optionalNumber(props.scheduling.maxSlots));
const cardElevation = computed(() => optionalNumber(props.scheduling.cardElevation));

const skipDirection = computed(() => {
  const startDate = props.scheduling.startDate;
  if (!startDate || closestDate.value === startDate) {
    return undefined;
  }
  return closestDate.value < startDate ? "past" : "future";
});
const closestDateLabel = computed(() => formatDatePattern(closestDate.value, "MMMM d", "MMMM d, yyyy", showYear.value));

const message = computed(() =>
  schedulingOptions(props.result, {
    showMultipleSchedulingTypes: props.scheduling.showMultipleSchedulingTypes,
    text: resultText,
  }),
);
const cardVariant = computed(() => message.value.card_variant || "flat");
const messageModifiers = computed(() => ({ "--titled": Boolean(message.value.title) }));

const showFullSchedule = computed(() => Boolean(closestDate.value) && !["grid", "cards"].includes(branch.value));

// Each booking link names the finder page it was picked from, for the booking's attribution.
function withReferral(bookingUrl) {
  const url = new URL(bookingUrl, window.location.href);
  url.searchParams.set("referring_url", window.location.toString());
  url.searchParams.set("referring_ui_component", "doctor_finder");
  return url.toString();
}

function withReferrals(entry) {
  return {
    ...entry,
    slots: (entry.slots ?? []).map((slot) => ({ ...slot, booking_url: withReferral(slot.booking_url) })),
  };
}

const entries = computed(() => (props.result.appointment_slots ?? []).map(withReferrals));

const slotFilters = computed(() => {
  if (props.result.resultType === "Location") {
    return { address_id: props.result.address_id };
  }
  return { physician_id: props.result.id, department_id: props.result.department_id };
});

const classModifiers = computed(() => ({
  [`--${branch.value}`]: Boolean(branch.value),
  "--multiple": Boolean(message.value.multiple),
}));

function handleCardsMore(event) {
  emit("show-more-slots", { focusDate: event.detail?.[0]?.date, filters: slotFilters.value });
}

function requestFullSchedule() {
  emit("show-more-slots", { filters: slotFilters.value });
}

function handleFullSchedule(event) {
  trackControlClick(event);
  requestFullSchedule();
}

function handleShowDate(event) {
  trackControlClick(event);
  emit("show-date", closestDate.value);
}
</script>

<style>
:host {
  display: block;
}
</style>

<style lang="scss" scoped>
@use "@assets/mixins/breakpoint" as *;

.sp-finder-result-scheduling {
  /* Layout */
  --gap: var(--sp-comp-finder-result-scheduling-gap, var(--sp-ref-scale-10));
  --card-padding: var(--sp-comp-finder-result-scheduling-card-padding, var(--sp-ref-scale-24));
  --card-min-height: var(--sp-comp-finder-result-scheduling-card-min-height, 12.25rem);
  --card-width: var(--sp-comp-finder-result-scheduling-card-width, 25rem);
  --message-width: var(--sp-comp-finder-result-scheduling-message-width, 350px);
  --message-gap: var(--sp-comp-finder-result-scheduling-message-gap, var(--gap));
  --message-direction: var(--sp-comp-finder-result-scheduling-message-direction, column);
  --message-text-align: var(--sp-comp-finder-result-scheduling-message-text-align, center);
  --divider-color: var(--sp-comp-finder-result-scheduling-divider-color, var(--sp-sys-color-outline-variant));

  /* Typography */
  --heading-font: var(--sp-comp-finder-result-scheduling-heading-font, var(--sp-sys-typescale-h4-short));
  --booking-number-font: var(
    --sp-comp-finder-result-scheduling-booking-number-font,
    var(--sp-sys-typescale-body-short)
  );

  /* Full schedule */
  --full-schedule-icon-size: var(--sp-comp-finder-result-scheduling-full-schedule-icon-size, 33px);
  --full-schedule-icon-color: var(
    --sp-comp-finder-result-scheduling-full-schedule-icon-color,
    var(--sp-sys-color-icon-filled)
  );
  --full-schedule-padding: var(--sp-comp-finder-result-scheduling-full-schedule-padding, 4px 15px);

  display: flex;
  flex-direction: column;
  gap: var(--sp-comp-finder-result-scheduling-row-gap, var(--sp-ref-scale-16));
  container: scheduling / inline-size;
  height: 100%;
  width: 100%;

  @include sp-breakpoint(sm) {
    align-items: center;
    justify-content: center;
  }

  &.--message {
    @include sp-breakpoint(sm) {
      align-items: flex-end;
    }
  }

  &.--cards {
    @include sp-breakpoint(sm) {
      align-items: var(--sp-comp-finder-result-scheduling-cards-alignment, flex-start);
      justify-content: flex-start;
    }
  }

  &__card {
    @include sp-breakpoint(sm) {
      --sp-comp-card-padding-inline: var(--card-padding);
      --sp-comp-card-padding-block: var(--card-padding);

      align-self: center;
      min-height: var(--card-min-height);
      min-width: var(--card-width);
    }
  }

  &:not(.--multiple) &__card {
    @include sp-breakpoint(sm) {
      width: 100%;
      max-width: var(--card-width);
      margin-bottom: var(--sp-ref-scale-24);
    }
  }

  &__walk-in {
    padding-bottom: var(--sp-ref-scale-4);
  }

  &__message {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--message-gap);
    margin: var(--gap) 0;
    text-align: center;
    width: 100%;

    @include sp-breakpoint(sm) {
      flex-direction: var(--message-direction);
      margin: var(--gap);
      max-width: var(--message-width);
      text-align: var(--message-text-align);
    }

    &.--titled {
      @include sp-breakpoint(sm) {
        flex-direction: column;
        text-align: center;
      }
    }

    &.--column {
      flex-direction: column;
      gap: var(--sp-ref-scale-18);
      text-align: center;
      margin: 0;
      max-width: none;
    }
  }

  &__patient-type {
    margin: 0;
    font: var(--heading-font);
  }

  &__columns {
    display: flex;
    flex-direction: column;
    gap: var(--sp-ref-scale-22);
    position: relative;
    width: 100%;

    .--column + .--column {
      border-top: 1px solid var(--divider-color);
      padding-top: var(--sp-ref-scale-22);
    }

    .sp-finder-result-scheduling__patient-type {
      font: var(--sp-sys-typescale-h4-small-short);
      font-weight: var(--sp-sys-font-weight-bold);
    }

    @container scheduling (width > 400px) {
      flex-direction: row;
      column-gap: calc(2 * var(--sp-ref-scale-44));

      .--column {
        flex: 1 1 0;
        min-width: 0;
      }

      .--column + .--column {
        border-top: 0;
        padding-top: 0;
      }

      .sp-finder-result-scheduling__patient-type {
        font: var(--heading-font);
        font-weight: var(--sp-sys-font-weight-bold);
      }

      &::before {
        content: "";
        position: absolute;
        left: calc(50% - 0.5px);
        width: 1px;
        height: 100%;
        background-color: var(--divider-color);
      }
    }
  }

  &__skip {
    display: flex;
    align-items: center;
    gap: var(--gap);
    margin: var(--gap) 0;

    @include sp-breakpoint(sm) {
      flex-direction: column;
      margin: var(--gap);
      max-width: var(--message-width);
      text-align: center;
    }
  }

  &__skip-icon {
    --sp-comp-icon-size: 45px;
  }

  &__skip-text {
    flex: 1 0 0%;
  }

  &__date {
    white-space: nowrap;
  }

  // The scheduling links take the button font unless a deployment names a link weight.
  &__skip-link,
  &__full-schedule {
    --sp-comp-button-font-short: var(--sp-comp-finder-result-scheduling-link-font-weight)
      var(--sp-comp-button-font-size, var(--sp-sys-typescale-body-font-size)) /
      var(--sp-comp-button-line-height, var(--sp-sys-typescale-body-line-height))
      var(--sp-comp-button-font-family, var(--sp-sys-typescale-body-font-family));
  }

  &__skip-link {
    --sp-comp-button-display: block;
    --sp-comp-button-appearance-link-text-decoration: var(
      --sp-comp-finder-result-scheduling-link-text-decoration,
      underline
    );
  }

  &__grid {
    --sp-comp-slot-grid-heading-display: var(--sp-comp-finder-result-grid-day-heading-display, none);
    --sp-comp-slot-grid-row-gap: var(--sp-comp-finder-result-grid-slot-gap, var(--sp-ref-scale-10));
  }

  &__full-schedule {
    --sp-comp-button-size: auto;
    --sp-comp-button-text-decoration: var(--sp-comp-finder-result-scheduling-link-text-decoration, none);
    --sp-comp-button-primary-variant-text-text-color: var(
      --sp-comp-button-appearance-link-text-color,
      var(--sp-sys-color-link)
    );
    --sp-comp-button-primary-hover-variant-text-bg-opacity: 0;
    --sp-comp-button-padding: var(--full-schedule-padding);
    --sp-comp-button-gap: 6px;

    white-space: nowrap;
  }

  &__full-schedule-icon {
    --sp-comp-icon-size: var(--full-schedule-icon-size);
    --sp-comp-icon-fill: var(--full-schedule-icon-color);
  }
}
</style>
